import { Table, TableBody, TableCell, TableHead, TableRow } from '@fox/atoms/src/table-atoms';
import { FlexRowCenterJustify } from '@fox/layout';
import { useTheme } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { useState } from 'react';
import styled from 'styled-components';
import { ResponsiveTableColumn, ResponsiveTablePaginationProps } from '../responsive-table/contracts';

const TableStyleWrapper = styled.div`
	.MuiTable-root {
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
	}
	.MuiTableHead-root {
		.MuiTableRow-head:first-child {
			border-top-left-radius: 12px;
			border-top-right-radius: 12px;
		}
	}
	.MuiTableRow:last-child {
		.MuiTableCell-root {
			&:first-child {
				border-bottom-left-radius: 12px;
			}
			&:last-child {
				border-bottom-right-radius: 12px;
			}
		}
	}
`;

export type TableSelectColumn<T> = Pick<ResponsiveTableColumn<T>, 'dataIndex' | 'title' | 'render'>;
interface TableSelectProps<T> {
	/**
	 * Array of column definitions for the table.
	 */
	columns: TableSelectColumn<T>[];
	/**
	 * Array of row data (objects with keys matching columns).
	 */
	data: T[];
	/**
	 * Callback invoked when a row is clicked.
	 */
	onSelect: (row: T) => void;
	/**
	 * Ensure rows get refreshed correctly by providing a unique key for each row.
	 */
	getRowKey: (row: T) => string;

	/**
	 * The initially selected row (optional).
	 */
	selectedRow?: T;
	/**
	 * Optional pagination props for controlling table pagination.
	 */
	paginationProps?: ResponsiveTablePaginationProps;
}

/**
 * TableSelect provides a clean, easy way to select a row from a table-like view, typically used on mobile and tablet.
 *
 * Each row is clickable and displays a selected state. Useful for scenarios where users need to pick one item from a list on touch devices.
 *
 * Props:
 * - columns: array of column definitions { dataIndex: string; title: string; render?: (row: T) => React.ReactNode }
 * - data: array of row data (object with dataIndices matching columns)
 * - onSelect: callback(row: T) when a row is clicked
 * - selectedRow: optional, the initially selected row
 */
export function TableSelect<T>({ columns, data, onSelect, selectedRow, paginationProps, getRowKey }: TableSelectProps<T>) {
	const [internalSelected, setInternalSelected] = useState<T | null>(selectedRow ?? null);
	const theme = useTheme();
	const handleRowClick = (row: T) => {
		setInternalSelected(row);
		onSelect(row);
	};

	return (
		<TableStyleWrapper>
			<Table sx={{ backgroundColor: theme.palette.common.white }}>
				<TableHead>
					<TableRow>
						{columns.map(col => (
							<TableCell sx={{ backgroundColor: theme.palette.grey[200] }} key={col.dataIndex}>
								{col.title}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody sx={{ backgroundColor: theme.palette.common.white }}>
					{data.map(row => {
						const isSelected = internalSelected === row;
						const rowKey = getRowKey(row);
						return (
							<TableRow key={rowKey} onClick={() => handleRowClick(row)} selected={isSelected} style={{ cursor: 'pointer' }}>
								{columns.map(col => {
									const value: string = col.dataIndex ? row[col.dataIndex] : '';
									const renderValue = col.render ? col.render(row) : value;
									return <TableCell key={`${rowKey}_${col.dataIndex}`}>{renderValue}</TableCell>;
								})}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
			{paginationProps ? (
				<FlexRowCenterJustify style={{ marginTop: '1rem' }}>
					<TableSelectPagination paginationProps={paginationProps} />
				</FlexRowCenterJustify>
			) : null}
		</TableStyleWrapper>
	);
}

function TableSelectPagination({ paginationProps }: { paginationProps: ResponsiveTablePaginationProps }) {
	const { itemsPerPage = 10, onPageChange, total, page } = paginationProps;

	return total <= itemsPerPage ? null : (
		<Pagination
			style={{ alignSelf: 'center' }}
			count={Math.ceil(total / itemsPerPage)}
			page={page}
			onChange={(_, value) => {
				onPageChange(value);
			}}
			color={'secondary'}
		/>
	);
}
